import React, { useEffect, useState } from "react";
import styles from "./FeatureModal.module.css";
import "./FeatureModal.animations.css";

export type FeatureModalType = "brandkit" | "variables" | "comments";

type Props = {
  isOpen: boolean;
  type: FeatureModalType | null;
  onClose: () => void;
  onSignup: () => void;
};

type EducationalSlide = {
  type?: undefined;
  tag: string;
  title: string;
  body: string;
  features?: string[];
};

type CtaSlide = {
  type: "cta";
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
};

type Slide = EducationalSlide | CtaSlide;

// Source-of-truth copy mirrored from the marketing widget HTML
// (flyer-builder-widget.html). The widget bakes a fourth "sign up"
// page directly into each modal's flow; we don't render that page
// here because clicking the persistent CTA in the footer opens the
// real SignupModal instead. The fourth slide below carries the same
// title/body/eyebrow as the widget's fourth-page sign-up copy so the
// transition into the SignupModal reads as one continuous prompt.
//
// `features` strings may contain `<strong>` (rendered via
// dangerouslySetInnerHTML in the renderer below) — that's how the
// widget styles the leading label of each bullet.
const FM_DATA: Record<FeatureModalType, { slides: Slide[] }> = {
  brandkit: {
    slides: [
      {
        tag: "Brand Kit",
        title: "Your brand, everywhere",
        body: "Upload your logo, colors, and fonts once. Every flyer will inherit your brand identity, perfectly applied.",
        features: [
          "<strong>Brand Colors</strong> Your palette applied to every element",
          "<strong>Typography</strong> Heading and body fonts, auto-set",
          "<strong>Logo &amp; Icons</strong> Placed on every template automatically",
        ],
      },
      {
        tag: "Applied automatically",
        title: "Brand-perfect flyers, instantly",
        body: "No more manually restyling every template. Select a design, and your Brand Kit is applied in one click.",
        features: [
          "Colors flow across all elements",
          "Logos correctly positioned",
          "Fonts set consistently across text styles",
        ],
      },
      {
        tag: "One kit, every campaign",
        title: "Consistent at scale",
        body: "Your brand looks identical on every flyer, every time. Upload your brand kit once and every campaign reflects it.",
        features: [
          "Same look and feel across campaigns",
          "Always use the right brand assets",
          "Changes to your kit apply to all future flyers",
        ],
      },
      {
        type: "cta",
        eyebrow: "Get started for free",
        title: "Upload your brand kit today",
        body: "Sign up for free and upload your assets. Your logo, colors, and fonts will be ready for your very first campaign.",
        cta: "Create free account →",
      },
    ],
  },
  variables: {
    slides: [
      {
        tag: "Variables",
        title: "Personalize every flyer",
        body: "Variables are smart placeholders, like First Name, City, and Discount, that fill in automatically each time your campaign runs.",
        features: ["Auto-filled from your audience data", "Set per campaign run", "Apply to any text on your flyer"],
      },
      {
        tag: "Default Variables",
        title: "Built-in personalization",
        body: "Every account includes powerful variables ready to use out of the box. No setup required, just drop them into your flyer.",
        features: ["Frequently used variables lists", "Drag and drop into designs", "Customize to your data sets"],
      },
      {
        tag: "Custom Variables",
        title: "Define your own",
        body: "Create any variable you need, like product names and expiration dates. Set it once and personalize thousands of flyers without extra effort.",
        features: [
          "{{Product}} Highlight specific items per city.",
          "{{Promo Code}} Unique codes per audience batch.",
          "{{Expiry Date}} Time-limited offers at scale.",
        ],
      },
      {
        type: "cta",
        eyebrow: "Personalize across assets",
        title: "Campaigns at scale",
        body: "Sign up free to create custom variables and run fully personalized campaigns across any city, any audience.",
        cta: "Create free account →",
      },
    ],
  },
  // Mirrors brandkit/variables shape: three educational slides
  // followed by a CTA slide whose `type: "cta"` makes the footer
  // render the signup-handoff button (same wiring as the other
  // modals).
  comments: {
    slides: [
      {
        tag: "Real-time Collaboration",
        title: "Your whole team, on the same canvas",
        body: "Everyone sees every change the moment it happens. Exchange ideas and iterate on your flyer design together, live, in one shared space.",
        features: [
          "Co-edit the shared canvas in real time",
          "No version confusion with everyone working on the same file",
          "Invite designers, marketers, and approvers to the design",
        ],
      },
      {
        tag: "Team Conversations",
        title: "Feedback flows as fast as ideas",
        body: "Comment threads live right next to the design. Your team replies, reacts, and iterates in seconds.",
        features: [
          "Threaded replies keep feedback in context",
          "Every suggestion visible to the whole team",
          "Conversation and creative stay in one place",
        ],
      },
      {
        tag: "Ship Together",
        title: "Aligned teams launch faster",
        body: "When designers, marketers, and approvers all collaborate in one tool, campaigns go from first draft to live in hours.",
        features: [
          "One shared workspace replaces email threads",
          "Decisions happen in the design, not in a meeting",
          "More campaigns, less coordination",
        ],
      },
      {
        type: "cta",
        eyebrow: "Collaborate for free",
        title: "Bring in your whole team",
        body: "Sign up free and invite your team today. Real-time collaboration, shared design access, and in-context feedback all included. No credit card required.",
        cta: "Create free account →",
      },
    ],
  },
};

// Strings are inlined via dangerouslySetInnerHTML; the CSS classes
// they reference live in FeatureModal.animations.css. Restart trick
// (in updateNav) re-mounts the active slide so CSS animations replay
// on tab change.
const FM_ANIMS: Record<FeatureModalType, string[]> = {
  brandkit: [
    `<div class="anim-bk0">
      <div class="bk0-circle">
        <div class="bk0-sw-row">
          <div class="bk0-sw bk0-sw-red"></div>
          <div class="bk0-sw bk0-sw-navy"></div>
          <div class="bk0-sw bk0-sw-gold"></div>
        </div>
        <div class="bk0-pal-wrap">
          <svg class="bk0-pal-icon" viewBox="0 0 24 24" fill="none" stroke="#a2a0fa" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="13.5" cy="6.5" r=".5" fill="#a2a0fa"/><circle cx="17.5" cy="10.5" r=".5" fill="#a2a0fa"/><circle cx="8.5" cy="7.5" r=".5" fill="#a2a0fa"/><circle cx="6.5" cy="12.5" r=".5" fill="#a2a0fa"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>
        </div>
        <div class="bk0-tools">
          <div class="bk0-tool bk0-tool-pink"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ee4360" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg></div>
          <div class="bk0-tool bk0-tool-blue"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4b6ef5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3l3.5 3.5"/><path d="M12.146 8.354a2 2 0 0 1 0 2.829L7 16.5l-4 1 1-4 5.317-5.317a2 2 0 0 1 2.829.171z"/><path d="M14 5l5 5"/></svg></div>
          <div class="bk0-tool bk0-tool-yellow"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#f9a825" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l1.8 5.4H19l-4.6 3.3 1.8 5.5L12 13l-4.2 3.2 1.8-5.5L5 7.4h5.2z" fill="#f9a825"/></svg></div>
        </div>
      </div>
      <svg class="bk0-spark bk0-spark1" viewBox="0 0 24 24" fill="#a2a0fa"><path d="M12 2l1.5 4.5H18l-3.8 2.8 1.5 4.5L12 11l-3.7 2.8 1.5-4.5L6 6.5h4.5z"/></svg>
      <svg class="bk0-spark bk0-spark2" viewBox="0 0 24 24" fill="#a2a0fa"><path d="M12 2l1.5 4.5H18l-3.8 2.8 1.5 4.5L12 11l-3.7 2.8 1.5-4.5L6 6.5h4.5z"/></svg>
    </div>`,
    `<div class="anim-bk1">
      <div class="bk1-palette">
        <div class="bk1-pal-header">Brand Kit</div>
        <div class="bk1-pal-colors">
          <div class="bk1-pc bk1-pc1"></div>
          <div class="bk1-pc bk1-pc2"></div>
          <div class="bk1-pc bk1-pc4"></div>
        </div>
        <div class="bk1-pal-typo">Aa</div>
      </div>
      <div class="bk1-arrow">→</div>
      <div class="bk1-flyer">
        <div class="bk1-fill"></div>
        <div class="bk1-flyer-logo">oppizi</div>
        <div class="bk1-accent"></div>
        <div class="bk1-lines">
          <div class="bk1-ln" style="width:80%"></div>
          <div class="bk1-ln" style="width:60%"></div>
          <div class="bk1-ln" style="width:72%"></div>
        </div>
      </div>
    </div>`,
    `<div class="anim-bk2">
      <div class="bk2-row">
        <div class="bk2-item">
          <div class="bk2-dh">
            <div class="bk2-hole"></div>
            <div class="bk2-fi">
              <div class="bk2-top"><span class="bk2-lm">oppizi</span></div>
              <div class="bk2-stripe"></div>
              <div class="bk2-body">
                <div class="bk2-l bk2-la"></div><div class="bk2-l bk2-lb"></div>
                <div class="bk2-l bk2-lc"></div><div class="bk2-l bk2-la"></div>
                <div class="bk2-l bk2-lb"></div>
              </div>
            </div>
          </div>
          <div class="bk2-label">Door Hanger</div>
          <div class="bk2-chk">✓</div>
        </div>
        <div class="bk2-item">
          <div class="bk2-portrait">
            <div class="bk2-top"><span class="bk2-lm">oppizi</span></div>
            <div class="bk2-stripe"></div>
            <div class="bk2-body">
              <div class="bk2-l bk2-la"></div><div class="bk2-l bk2-lb"></div><div class="bk2-l bk2-lc"></div>
            </div>
          </div>
          <div class="bk2-label">Portrait A5</div>
          <div class="bk2-chk">✓</div>
        </div>
        <div class="bk2-item">
          <div class="bk2-landscape">
            <div class="bk2-top"></div>
            <div class="bk2-stripe"></div>
            <div class="bk2-body">
              <div class="bk2-l bk2-la"></div><div class="bk2-l bk2-lb"></div><div class="bk2-l bk2-lc"></div>
            </div>
          </div>
          <div class="bk2-label">Landscape A5</div>
          <div class="bk2-chk">✓</div>
        </div>
      </div>
    </div>`,
    `<div class="anim-cta">
      <div class="cta-ring"></div><div class="cta-ring"></div><div class="cta-ring"></div>
      <div class="cta-hub"><span class="cta-hub-logo">oppizi</span></div>
      <span class="cta-star">✦</span><span class="cta-star">✦</span>
      <span class="cta-star">✦</span><span class="cta-star">✦</span>
      <span class="cta-star">✦</span><span class="cta-star">✦</span>
    </div>`,
  ],
  variables: [
    `<div class="anim-var0">
      <div class="var0-flyer">
        <div class="var0-fl-head">
          <div class="var0-fl-logo">oppizi</div>
          <div class="var0-fl-tagline">Creative Studio</div>
        </div>
        <div class="var0-fl-stripe"></div>
        <div class="var0-fl-body">
          <div class="var0-fl-row">
            <span class="var0-fl-txt">Hi,</span>
            <div class="var0-chip-wrap var0-cw1">
              <div class="var0-val var0-v1">Sarah</div>
              <div class="var0-val var0-v2">Mike</div>
              <div class="var0-val var0-v3">Cody</div>
              <div class="var0-val var0-v4">Emma</div>
            </div>
            <span class="var0-fl-txt">— your offer:</span>
          </div>
          <div class="var0-fl-row">
            <div class="var0-chip-wrap var0-cw3">
              <div class="var0-val var0-v1">20% OFF</div>
              <div class="var0-val var0-v2">30% OFF</div>
              <div class="var0-val var0-v3">15% OFF</div>
              <div class="var0-val var0-v4">25% OFF</div>
            </div>
            <span class="var0-fl-txt">on your next campaign</span>
          </div>
          <div class="var0-fl-row">
            <span class="var0-fl-txt">Available in</span>
            <div class="var0-chip-wrap var0-cw2">
              <div class="var0-val var0-v1">New York</div>
              <div class="var0-val var0-v2">London</div>
              <div class="var0-val var0-v3">Paris</div>
              <div class="var0-val var0-v4">Sydney</div>
            </div>
          </div>
        </div>
      </div>
    </div>`,
    `<div class="anim-var1">
      <div class="var1-dd">
        <div class="var1-dd-hdr">
          <span class="var1-dd-title">Default Variables</span>
          <span class="var1-dd-info">i</span>
        </div>
        <div class="var1-dd-panel">
          <div class="var1-dd-sel">
            <span class="var1-dd-sel-txt">Select Variables</span>
          </div>
          <div class="var1-dd-list">
            <div class="var1-dd-inner">
              <div class="var1-dd-item">First Name</div>
              <div class="var1-dd-item">City</div>
              <div class="var1-dd-item">State</div>
              <div class="var1-dd-item">ZIP</div>
              <div class="var1-dd-item">QR Code</div>
              <div class="var1-dd-item">Coupon 1</div>
              <div class="var1-dd-item">Coupon 2</div>
            </div>
          </div>
        </div>
      </div>
    </div>`,
    `<div class="anim-var2">
      <div class="var2-upload">
        <div class="var2-upload-card">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#1a2547" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="16 16 12 12 8 16"></polyline>
            <line x1="12" y1="12" x2="12" y2="21"></line>
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path>
          </svg>
          <span class="var2-upload-lbl">Upload Custom Variables</span>
          <span class="var2-upload-sub">Drop your .csv or .xlsx here</span>
        </div>
      </div>
      <div class="var2-done">
        <div class="var2-done-circle">✓</div>
        <div class="var2-done-lbl">Custom variables uploaded</div>
      </div>
      <div class="var2-typing">
        <div class="var2-field">
          <span class="var2-typed">{{Product}}</span>
          <span class="var2-cursor">|</span>
        </div>
        <div class="var2-chip-result">{{Product}}</div>
        <div class="var2-ok">✓</div>
      </div>
    </div>`,
    `<div class="anim-cta">
      <div class="cta-ring"></div><div class="cta-ring"></div><div class="cta-ring"></div>
      <div class="cta-hub"><span class="cta-hub-logo">oppizi</span></div>
      <span class="cta-star">✦</span><span class="cta-star">✦</span>
      <span class="cta-star">✦</span><span class="cta-star">✦</span>
      <span class="cta-star">✦</span><span class="cta-star">✦</span>
    </div>`,
  ],
  //   cm0 — flyer with collaborator pins popping in
  //   cm1 — comment thread with sequential replies
  //   cm2 — central design with teammates orbiting around it
  //   anim-cta — same shared CTA animation as brandkit/variables
  comments: [
    `<div class="anim-cm0">
      <div class="cm0-scene">
        <div class="cm0-flyer">
          <div class="cm0-flyer-hdr"></div>
          <div class="cm0-flyer-stripe"></div>
          <div class="cm0-flyer-bd">
            <div class="cm0-flyer-ln" style="width:85%"></div>
            <div class="cm0-flyer-ln" style="width:65%"></div>
            <div class="cm0-flyer-img"></div>
            <div class="cm0-flyer-ln" style="width:75%"></div>
            <div class="cm0-flyer-ln" style="width:50%"></div>
            <div class="cm0-flyer-ln" style="width:60%"></div>
          </div>
        </div>
        <div class="cm0-pin cm0-pin1"><div class="cm0-bubble" style="background:#4CAF82;"><span>S</span></div></div>
        <div class="cm0-pin cm0-pin2"><div class="cm0-bubble" style="background:#4B6EF5;"><span>A</span></div></div>
        <div class="cm0-pin cm0-pin3"><div class="cm0-bubble" style="background:#f9a825;"><span>J</span></div></div>
        <div class="cm0-pin cm0-pin4"><div class="cm0-bubble" style="background:#EE4360;"><span>M</span></div></div>
        <div class="cm0-live">
          <div class="cm0-live-dot"></div>
          <span class="cm0-live-txt">4 collaborating</span>
        </div>
      </div>
    </div>`,
    `<div class="anim-cm1">
      <div class="cm1-thread">
        <div class="cm1-hdr">
          <span class="cm1-hdr-title">On the headline</span>
          <div class="cm1-hdr-avs">
            <div class="cm1-hdr-av" style="background:#4CAF82;">S</div>
            <div class="cm1-hdr-av" style="background:#4B6EF5;">A</div>
            <div class="cm1-hdr-av" style="background:#f9a825;">J</div>
          </div>
        </div>
        <div class="cm1-body">
          <div class="cm1-msg cm1-msg1">
            <div class="cm1-av cm1-av-g">S</div>
            <div class="cm1-msg-body">
              <div class="cm1-msg-author">Sarah</div>
              <div class="cm1-msg-text">Can we try a bolder font here?</div>
            </div>
          </div>
          <div class="cm1-msg cm1-msg2">
            <div class="cm1-av cm1-av-b">A</div>
            <div class="cm1-msg-body">
              <div class="cm1-msg-author">Alex</div>
              <div class="cm1-msg-text">On it! Updating now 🎨</div>
            </div>
          </div>
          <div class="cm1-msg cm1-msg3">
            <div class="cm1-av cm1-av-o">J</div>
            <div class="cm1-msg-body">
              <div class="cm1-msg-author">Jamie</div>
              <div class="cm1-msg-text">Looks amazing — ship it 🚀</div>
            </div>
          </div>
        </div>
      </div>
    </div>`,
    `<div class="anim-cm2">
      <div class="cm2-hub">
        <div class="cm2-center">
          <div class="cm2-center-hdr"></div>
          <div class="cm2-center-stripe"></div>
          <div class="cm2-center-bd">
            <div class="cm2-center-ln" style="width:80%"></div>
            <div class="cm2-center-ln" style="width:60%"></div>
            <div class="cm2-center-ln" style="width:70%"></div>
            <div class="cm2-center-ln" style="width:50%"></div>
          </div>
        </div>
        <div class="cm2-spoke cm2-spoke1"></div>
        <div class="cm2-spoke cm2-spoke2"></div>
        <div class="cm2-spoke cm2-spoke3"></div>
        <div class="cm2-spoke cm2-spoke4"></div>
        <div class="cm2-av-wrap cm2-av-wrap1">
          <div class="cm2-av" style="background:#4CAF82;">S</div>
          <div class="cm2-av-label">Sarah</div>
        </div>
        <div class="cm2-av-wrap cm2-av-wrap2">
          <div class="cm2-av" style="background:#4B6EF5;">A</div>
          <div class="cm2-av-label">Alex</div>
        </div>
        <div class="cm2-av-wrap cm2-av-wrap3">
          <div class="cm2-av" style="background:#f9a825;">J</div>
          <div class="cm2-av-label">Jamie</div>
        </div>
        <div class="cm2-av-wrap cm2-av-wrap4">
          <div class="cm2-av" style="background:#EE4360;">M</div>
          <div class="cm2-av-label">Maya</div>
        </div>
        <div class="cm2-counter">4 teammates on this design</div>
      </div>
    </div>`,
    `<div class="anim-cta">
      <div class="cta-ring"></div><div class="cta-ring"></div><div class="cta-ring"></div>
      <div class="cta-hub"><span class="cta-hub-logo">oppizi</span></div>
      <span class="cta-star">✦</span><span class="cta-star">✦</span>
      <span class="cta-star">✦</span><span class="cta-star">✦</span>
      <span class="cta-star">✦</span><span class="cta-star">✦</span>
    </div>`,
  ],
};

const ChevronLeft = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);
const ChevronRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

export const FeatureModal: React.FC<Props> = ({ isOpen, type, onClose, onSignup }) => {
  const [active, setActive] = useState(false);
  const [current, setCurrent] = useState(0);

  // Reset slide when type changes
  useEffect(() => {
    if (isOpen && type) setCurrent(0);
  }, [isOpen, type]);

  // Two-frame raf so the transition has a starting state to animate from.
  useEffect(() => {
    if (!isOpen) {
      setActive(false);
      return;
    }
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => setActive(true));
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, [isOpen]);

  // ESC closes
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // Restart CSS animations in the slot that's just become active. Each
  // anim element has an `anim-*` class with infinite keyframes; without
  // the clone trick the animation continues from wherever it was when the
  // user navigates to a different slide and back, instead of replaying.
  useEffect(() => {
    if (!isOpen || !type) return;
    // requestAnimationFrame so the React render has committed the new
    // `.active` class before we touch the DOM.
    const r = requestAnimationFrame(() => {
      document.querySelectorAll<HTMLElement>(`.${styles.fmAnimSlot}.${styles.active}`).forEach((slot) => {
        slot.querySelectorAll<HTMLElement>('[class*="anim-"]').forEach((el) => {
          const clone = el.cloneNode(true);
          el.parentNode?.replaceChild(clone, el);
        });
      });
    });
    return () => cancelAnimationFrame(r);
  }, [current, isOpen, type]);

  if (!isOpen || !type) return null;

  const data = FM_DATA[type];
  const anims = FM_ANIMS[type];
  const slides = data.slides;
  const isLast = current === slides.length - 1;

  const overlayClass = [styles.featureModal, styles.visible, active ? styles.active : ""].filter(Boolean).join(" ");

  return (
    <div className={overlayClass} onClick={onClose}>
      <div className={styles.fmCard} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={styles.fmCloseBtn}
          onClick={onClose}
          aria-label="Close"
          data-cypress-id="feature-modal-close"
        >
          ✕
        </button>

        <div className={styles.fmBody}>
          <div className={styles.fmBlueCol}>
            <div className={styles.fmBluePanel}>
              {slides.map((_, i) => {
                const animHtml = anims[i] || "";
                const slotClass = [styles.fmAnimSlot, i === current ? styles.active : ""].filter(Boolean).join(" ");
                return (
                  <div key={i} className={slotClass} data-slide={i} dangerouslySetInnerHTML={{ __html: animHtml }} />
                );
              })}
            </div>
            <div className={styles.fmNavLeft}>
              <button
                type="button"
                className={styles.fmIconBtn}
                onClick={() => setCurrent((c) => Math.max(0, c - 1))}
                disabled={current === 0}
                aria-label="Previous"
                data-cypress-id="feature-modal-prev"
              >
                <ChevronLeft />
              </button>
              <div className={styles.fmDots}>
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`${styles.fmDot} ${i === current ? styles.active : ""}`}
                    onClick={() => setCurrent(i)}
                    aria-label={`Slide ${i + 1}`}
                    data-cypress-id={`feature-modal-dot-${i}`}
                  />
                ))}
              </div>
              <button
                type="button"
                className={styles.fmIconBtn}
                onClick={() => setCurrent((c) => Math.min(slides.length - 1, c + 1))}
                disabled={isLast}
                aria-label="Next"
                data-cypress-id="feature-modal-next"
              >
                <ChevronRight />
              </button>
            </div>
          </div>

          <div className={styles.fmWhiteCol}>
            <div className={styles.fmWhitePanel}>
              {slides.map((slide, i) => {
                const slotClass = [styles.fmTextSlot, i === current ? styles.active : ""].filter(Boolean).join(" ");
                if (slide.type === "cta") {
                  // CTA slides used to ship with their own inline button
                  // ("Create free account" / "Sign Up Free"). We dropped
                  // them in favor of a single always-visible footer
                  // button (`fmCtaPersistent` below) that takes over the
                  // CTA role on the last slide. Keeps the eyebrow/title/
                  // body messaging for context.
                  return (
                    <div key={i} className={slotClass} data-slide={i}>
                      <div className={styles.fmCtaSlideText}>
                        <div className={styles.fmCtaSlideEyebrow}>{slide.eyebrow}</div>
                        <div className={styles.fmCtaSlideTitle}>{slide.title}</div>
                        <div className={styles.fmCtaSlideBody}>{slide.body}</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={i} className={slotClass} data-slide={i}>
                    <div className={styles.fmSlideText}>
                      <div className={styles.fmSlideTag}>{slide.tag}</div>
                      <div className={styles.fmSlideTitle}>{slide.title}</div>
                      <div className={styles.fmSlideBody}>{slide.body}</div>
                      {slide.features && (
                        <div className={styles.fmSlideFeatures}>
                          {slide.features.map((f, j) => (
                            <div key={j} className={styles.fmSlideFeature}>
                              <div className={styles.fmFeatDot}>✓</div>
                              {/* Feature strings may contain inline
                                  `<strong>` labels (mirrors the
                                  widget HTML markup). `f` is hard-
                                  coded in FM_DATA above — no user
                                  input flows in — so the dangerously-
                                  set HTML is safe. */}
                              <span dangerouslySetInnerHTML={{ __html: f }} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.fmNavRight}>
              {/* Persistent footer CTA. Visible on every slide. Reads
                  "Skip and sign up for free" while there are still
                  slides ahead (it doubles as a skip-to-end shortcut),
                  and flips to a plain "Sign up for free" on the last
                  slide where there's nothing left to skip. */}
              <button
                type="button"
                className={styles.fmCtaPersistent}
                onClick={onSignup}
                data-cypress-id="feature-modal-cta-skip"
              >
                {isLast ? "Sign up for free" : "Skip and sign up for free"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureModal;
